import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, MenuItem, Popover } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { debounce, find, isEmpty, isNil, map } from 'lodash';
import { LoadingButton } from '@mui/lab';

import { OrderFillType, SortKey } from 'enums/common.enum';
import OrderStatus from 'enums/order.enum';

import SelectField from 'common/forms/SelectField';
import DateField from 'common/forms/DateField';
import TextField from 'common/forms/TextField';
import { buildCatalogProductOptionRenderer } from 'common/forms/CatalogProductOptionRenderer';
import { validateMinLength } from 'common/forms/formValidations';
import { NDC_FILTER_TOOLTIP } from 'modules/WorkQueue/constants/filterTooltips';
import { listCatalogProductsByDrugName } from 'store/thunks/drugThunks';
import { filterOrderLineItems } from 'store/thunks/workQueueThunks';
import { EnableInventoryLookup, casingFormat } from 'common/utils';
import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';

import FacilityFilterField from './FacilityFilterField';
import { useTransferFacilityContext } from './MultiTransferFacilityButton';

const OrderLineItemsFilterButton = () => {
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  const dispatch = useDispatch();
  const { clearTransferItems } = useTransferFacilityContext();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  // POM-6124: free-text NDC list, same shape as the Bulk queue filter.
  const [selectedNdcs, setSelectedNdcs] = useState([]);
  // POM-6124: catalog-backed Drug Name selections, live only when EnableInventoryLookup is on.
  const [drugLoading, setDrugLoading] = useState(false);
  const [drugNameOptions, setDrugNameOptions] = useState([]);
  const [drugNameInputValue, setDrugNameInputValue] = useState('');
  const [selectedDrugProducts, setSelectedDrugProducts] = useState([]);
  const [selectedDrugProductObjects, setSelectedDrugProductObjects] = useState([]);
  const loading = useSelector((state) => state.workQueue.orderLineItems.loading);

  const selectedDrugProductsData = useSelector(
    ({ workQueue }) => workQueue.orderLineItems.filter?.selectedDrugProducts || []
  );

  const filter = useSelector(({ workQueue: { orderLineItems } }) => ({
    ...orderLineItems.filter,
    requestedDate:
      orderLineItems.filter?.requestedDate && parseISO(orderLineItems.filter.requestedDate),
    shipByDate: orderLineItems.filter?.shipByDate && parseISO(orderLineItems.filter.shipByDate),
  }));

  const popoverId = anchorEl ? 'WorkQueue-OrderFilterButton-Popover' : undefined;

  const partner = useMemo(() => {
    return map(partners, ({ partnerName }) => casingFormat(partnerName));
  }, [partners]);

  useEffect(() => {
    if (isEmpty(filter.partnerIds)) {
      setSelectedPartners([]);
      setSelectedPartnerIds([]);
    }
  }, [filter.partnerIds]);

  useEffect(() => {
    if (isEmpty(filter.ndcs)) {
      setSelectedNdcs([]);
    }
  }, [filter.ndcs]);

  // POM-6124: with the inventory lookup on, Drug Name options come from the catalog (the product
  // master) and a selection resolves to productIds rather than a name string.
  // listCatalogProductsByDrugName is already scoped to the roles that work this queue.
  const debouncedSearchDrugName = useMemo(() => {
    const searchFunc = (drugName) => {
      setDrugLoading(true);
      dispatch(listCatalogProductsByDrugName(drugName))
        .then((result) => {
          if (result) {
            setDrugNameOptions(result);
          }
        })
        .finally(() => setDrugLoading(false));
    };

    return debounce(searchFunc, 500, { trailing: true, leading: false });
  }, [dispatch]);

  // Collapse the catalog rows into one option per display name: the same product can appear
  // several times (different pack sizes / dosage forms share a product_id, and a product_id is
  // not unique in the catalog), which would otherwise render as duplicate identical options.
  // Grouped on the DB display_name (full name incl. pack size) so pack-size variants stay
  // distinct — same as the Bulk queue.
  const drugProductOptionGroups = useMemo(() => {
    if (!EnableInventoryLookup) return [];

    const groupsByLabel = drugNameOptions.reduce((acc, product) => {
      if (!product?.productId) return acc;

      const label = product.displayName;
      const existing = acc.get(label);

      if (existing) {
        if (!existing.productIds.includes(product.productId)) {
          existing.productIds.push(product.productId);
        }
        // POM-6309: a group spans several catalog rows, so it only reads as disabled when every
        // row behind it is — one enabled row still makes the drug orderable.
        existing.isCatalogEnabled = existing.isCatalogEnabled || product.isCatalogEnabled !== false;
        return acc;
      }

      acc.set(label, {
        label,
        productIds: [product.productId],
        isCatalogEnabled: product.isCatalogEnabled !== false,
      });
      return acc;
    }, new Map());

    return [...groupsByLabel.values()];
  }, [drugNameOptions]);

  useEffect(() => {
    if (drugNameInputValue.length >= 3) {
      debouncedSearchDrugName(drugNameInputValue);
    }
  }, [drugNameInputValue, debouncedSearchDrugName]);

  const getDrugNameNoOptionsText = () => {
    if (drugLoading) return 'Loading...';
    if (drugNameInputValue.length < 3) return 'Type at least 3 characters to search...';
    return 'No results found';
  };

  const handlePartnerSelect = (newValue) => {
    const partnerIds = newValue.map(
      (partnerName) =>
        partners.find((partnerData) => casingFormat(partnerData.partnerName) === partnerName)
          ?.partnerUuid
    );
    setSelectedPartners(newValue);
    setSelectedPartnerIds(partnerIds);
  };

  const handleOpen = (event) => {
    if (filter?.partnerIds) {
      setSelectedPartnerIds(filter.partnerIds);
      setSelectedPartners(
        filter.partnerIds.map((partnerId) =>
          casingFormat(find(partners, { partnerUuid: partnerId })?.partnerName)
        )
      );
    }

    if (filter?.ndcs) {
      setSelectedNdcs(filter.ndcs);
    }

    if (isEmpty(selectedDrugProductsData)) {
      setDrugNameOptions([]);
      setDrugNameInputValue('');
      setSelectedDrugProductObjects([]);
      setSelectedDrugProducts([]);
    } else {
      setSelectedDrugProductObjects(selectedDrugProductsData);
      setSelectedDrugProducts(selectedDrugProductsData.map((product) => product.label));
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    // `drugProducts` holds display labels for the autocomplete only — the identities they
    // resolve to travel in selectedDrugProducts.
    const { drugProducts, ...restFormData } = formData;

    const selectedDrugProductData = !isEmpty(selectedDrugProductObjects)
      ? selectedDrugProductObjects.map((product) => ({
          label: product.label,
          productIds: product.productIds,
        }))
      : [];

    clearTransferItems();
    dispatch(
      filterOrderLineItems({
        filter: {
          ...restFormData,
          requestedDate: formData.requestedDate && format(formData.requestedDate, 'yyyy-MM-dd'),
          shipByDate: formData.shipByDate && format(formData.shipByDate, 'yyyy-MM-dd'),
          partnerIds: !isEmpty(selectedPartnerIds) ? selectedPartnerIds : undefined,
          ndcs: !isEmpty(selectedNdcs) ? selectedNdcs : undefined,
          // Omitted rather than sent as [] when nothing is selected: the status bar decides
          // whether to show "Remove All" via compact(values(filter)), and an empty array is
          // truthy there — it would read as an active filter.
          selectedDrugProducts: !isEmpty(selectedDrugProductData)
            ? selectedDrugProductData
            : undefined,
          // With the lookup on, Drug Name filters by product identity, so the free-text name must
          // not also be sent — core ANDs its clauses and the two would conflict.
          itemName: !EnableInventoryLookup ? formData.itemName : undefined,
          ...(!isNil(formData.isReshipment) && { isReshipment: formData.isReshipment }),
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} direction='column'>
          <Form
            initialValues={filter}
            onSubmit={handleApplyFilter}
            render={({ handleSubmit, invalid }) => (
              <form onSubmit={handleSubmit} noValidate>
                <Grid item sx={{ py: 0.5 }}>
                  <TextField
                    label='Patient'
                    name={SortKey.PATIENT_NAME}
                    id='WorkQueue-OrderFilterButton-patientName-input'
                    validations={[validateMinLength('Minimum of 3 characters', 3)]}
                  />
                </Grid>
                <Grid item sx={{ py: 0.5 }}>
                  <TextField
                    label='Order Id'
                    name={SortKey.ORDER_ID}
                    id='WorkQueue-OrderFilterButton-orderId-input'
                  />
                </Grid>
                <Grid item sx={{ py: 0.5 }}>
                  <FacilityFilterField />
                </Grid>
                <Grid item sx={{ py: 0.5 }}>
                  <AutoCompleteMultipleSelect
                    label='Partner'
                    name={SortKey.PARTNER_IDS}
                    id='WorkQueue-OrderFilterButton-partnerIds-input'
                    multiple
                    defaultValue={selectedPartners}
                    options={partner}
                    sortOptions
                    value={selectedPartners}
                    onChange={(event, newValue) => handlePartnerSelect(newValue)}
                  />
                </Grid>

                <Grid item sx={{ py: 0.5 }}>
                  <TextField
                    label='Partner Account'
                    name={SortKey.PARTNER_ACCOUNT}
                    id='WorkQueue-OrderFilterButton-partnerAccount-input'
                  />
                </Grid>

                {/*
                 * POM-6124: with the inventory lookup on, Drug Name is a catalog-backed
                 * autocomplete resolving to productIds (same as the Bulk queue). It stays a plain
                 * text field while the switch is off, where nothing carries a product_id and the
                 * free-text itemName match is the only drug filter that can resolve.
                 */}
                {EnableInventoryLookup ? (
                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Drug Name'
                      name='drugProducts'
                      id='WorkQueue-OrderFilterButton-drugProduct-input'
                      multiple
                      // The catalog search already normalises spacing and punctuation on both
                      // sides; MUI's default filter does not, so left on it would hide results
                      // the server just matched (typing "Cabergoline0.5mg" drops the
                      // "Cabergoline 0.5MG" option).
                      serverSideSearch
                      defaultValue={selectedDrugProducts}
                      value={selectedDrugProducts}
                      loading={drugLoading}
                      options={drugProductOptionGroups.map((group) => group.label)}
                      renderOption={buildCatalogProductOptionRenderer(drugProductOptionGroups)}
                      sortOptions
                      onInputChange={(event, value) => {
                        setDrugNameInputValue(value ?? '');
                      }}
                      onChange={(event, newValue) => {
                        // Options are re-fetched on every keystroke, so a previously chosen label
                        // may no longer be in the current result set — carry the existing
                        // selections forward or removing one option would drop the others.
                        const availableGroups = [
                          ...drugProductOptionGroups,
                          ...selectedDrugProductObjects,
                        ];
                        const selectedObjects = newValue
                          .map((label) => availableGroups.find((group) => group.label === label))
                          .filter(Boolean);

                        setSelectedDrugProductObjects(selectedObjects);
                        setSelectedDrugProducts(newValue);
                      }}
                      noOptionsText={getDrugNameNoOptionsText()}
                    />
                  </Grid>
                ) : (
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Drug Name'
                      name={SortKey.ITEM_NAME}
                      id='WorkQueue-OrderFilterButton-itemName-input'
                    />
                  </Grid>
                )}

                <Grid item sx={{ py: 0.5 }}>
                  <AutoCompleteMultipleSelect
                    label='NDC'
                    name='ndcs'
                    id='WorkQueue-OrderFilterButton-ndcs-input'
                    multiple
                    freeSolo
                    options={[]}
                    formatOption={(value) => value}
                    defaultValue={selectedNdcs}
                    value={selectedNdcs}
                    onChange={(event, newValue) => setSelectedNdcs(newValue)}
                    labelTooltip={NDC_FILTER_TOOLTIP}
                  />
                </Grid>

                <Grid item sx={{ py: 0.5 }}>
                  <SelectField
                    label='Fill Type'
                    name={SortKey.FILL_TYPE}
                    id='WorkQueue-OrderFilterButton-fillType-dropdown'
                  >
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value={OrderFillType.FIRST_FILL}>First Fill</MenuItem>
                    <MenuItem value={OrderFillType.REFILL}>Refill</MenuItem>
                  </SelectField>
                </Grid>

                <Grid item sx={{ py: 0.5 }}>
                  <DateField
                    label='Requested Date'
                    name={SortKey.REQUESTED_DATE}
                    id='WorkQueue-OrderFilterButton-requestedDate-dateField'
                    maxDate={new Date()}
                  />
                </Grid>

                <Grid item sx={{ py: 0.5 }}>
                  <DateField
                    label='Ship By Date'
                    name={SortKey.SHIP_BY_DATE}
                    id='WorkQueue-OrderFilterButton-shipByDate-dateField'
                  />
                </Grid>

                <Grid item sx={{ py: 0.5 }}>
                  <SelectField
                    label='Status'
                    name={SortKey.ORDER_STATUS}
                    id='WorkQueue-OrderFilterButton-orderStatus-dropdown'
                  >
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value={OrderStatus.OPEN}>Open</MenuItem>
                    <MenuItem value={OrderStatus.ADDED_TO_SHIPMENT}>Shipment Created</MenuItem>
                    <MenuItem value={OrderStatus.SHIPPED}>Shipped</MenuItem>
                    <MenuItem value={OrderStatus.TRANSFERRED}>Transferred</MenuItem>
                    <MenuItem value={OrderStatus.ORDER_VERIFICATION}>Order Verification</MenuItem>
                    <MenuItem value={OrderStatus.FILL_LATER}>Fill Later</MenuItem>
                  </SelectField>
                </Grid>

                <Grid item sx={{ py: 0.5 }}>
                  <SelectField
                    label='Reshipments'
                    name='isReshipment'
                    id='WorkQueue-OrderFilterButton-reshipment-dropdown'
                  >
                    <MenuItem value={undefined}>All</MenuItem>
                    <MenuItem value>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </SelectField>
                </Grid>

                <Grid container justifyContent='flex-end'>
                  <LoadingButton
                    loading={loading}
                    type='submit'
                    variant='contained'
                    disabled={invalid}
                  >
                    Apply
                  </LoadingButton>
                </Grid>
              </form>
            )}
          />
        </Grid>
      </Popover>
    </>
  );
};

export default OrderLineItemsFilterButton;
